/** Order, payment, and shift domain types. */
/** Ingredient id from a stored swap modifier (`ing_swap:{ingredientId}`). */
export function swapModifierIngredientId(groupId) {
    if (!groupId.startsWith('ing_swap:'))
        return null;
    return groupId.slice('ing_swap:'.length);
}
/**
 * Human-readable option labels for a cart/order line: recipe choices, swap
 * defaults or substitutes, and modifier add-ons. Requires `product` to resolve
 * default swap ingredients when the cashier kept the recipe default.
 */
export function orderItemOptionLabels(item, product, swapDefaultLabel = 'Default:') {
    const labels = [];
    for (const m of item.modifiers) {
        if (m.groupId.startsWith('choice_group:'))
            labels.push(m.name);
    }
    const swapGroups = new Map();
    for (const swap of product?.ingredientSwaps ?? []) {
        if (!swapGroups.has(swap.ingredientId)) {
            swapGroups.set(swap.ingredientId, swap.ingredientName);
        }
    }
    for (const [ingId, ingredientName] of swapGroups) {
        const mod = item.modifiers.find((m) => swapModifierIngredientId(m.groupId) === ingId);
        if (mod)
            labels.push(mod.name);
        else
            labels.push(`${swapDefaultLabel} ${ingredientName}`);
    }
    for (const m of item.modifiers) {
        if (m.groupId.startsWith('mod_group:'))
            labels.push(m.name);
    }
    return labels;
}
/** Normalized card tip in AMD (0 when absent). */
export function orderTipAmount(tipAmount) {
    const n = tipAmount != null ? Number(tipAmount) : 0;
    return n > 0 ? n : 0;
}
/** Amount expected on payment rows: goods total + card tip. */
export function orderExpectedCharge(total, tipAmount) {
    return Number(total) + orderTipAmount(tipAmount);
}
/** Rounded AMD tolerance for payment vs expected charge comparisons. */
export const ORDER_PAYMENT_TOLERANCE_AMD = 1;
/** Sum of order line totals before loyalty (after per-line discounts). */
export function orderLineItemsSubtotal(items) {
    return items.reduce((s, it) => s + it.unit_price * it.quantity - (it.discount ?? 0), 0);
}
/** Loyalty discount stored on the order (percent tier + redeemed points), in AMD. */
export function orderLoyaltyDiscountAmt(order) {
    return Math.max(0, Number(order.loyalty_discount_amd) || 0);
}
/** Items subtotal before loyalty — should match line-item sum when data is consistent. */
export function orderSubtotalBeforeLoyalty(amountDue, loyaltyDiscountAmd) {
    return Number(amountDue) + orderLoyaltyDiscountAmt({ loyalty_discount_amd: loyaltyDiscountAmd });
}
/** True when line items don't reconcile with amount due + loyalty discount. */
export function orderItemsLoyaltyMismatch(itemsSubtotal, amountDue, loyaltyDiscountAmd) {
    return (Math.abs(itemsSubtotal - orderSubtotalBeforeLoyalty(amountDue, loyaltyDiscountAmd)) >=
        ORDER_PAYMENT_TOLERANCE_AMD);
}
/** True when recorded payments don't match goods + tip. */
export function orderPaymentMismatch(paymentTotal, orderTotal, tipAmount) {
    return (Math.abs(paymentTotal - orderExpectedCharge(orderTotal, tipAmount)) >=
        ORDER_PAYMENT_TOLERANCE_AMD);
}
/** Fiscal `total` from HDM response JSON, or null when absent. */
export function fiscalReceiptTotal(fiscal) {
    if (!fiscal || typeof fiscal !== 'object')
        return null;
    const n = Number(fiscal.total);
    return Number.isFinite(n) ? n : null;
}
/** True when HDM fiscal total doesn't match what we charged (payments). */
export function orderFiscalMismatch(paymentTotal, fiscal) {
    const fiscalTotal = fiscalReceiptTotal(fiscal);
    if (fiscalTotal == null)
        return false;
    return Math.abs(fiscalTotal - paymentTotal) >= ORDER_PAYMENT_TOLERANCE_AMD;
}
//# sourceMappingURL=order.js.map